(() => {
  'use strict';
  const OLD = '52631230775578';
  const NEW = '53219249291546';
  const PFX = new Set(['52913870864666', '52907314413850']);
  const XR_BUNDLE = '52602439762202';
  const LEGACY = new Set(['50173775610138', '49415107838234']);
  const id = line => String(line.variant_id || line.id);
  const stable = value => JSON.stringify(value, (_, item) => item && typeof item === 'object' && !Array.isArray(item) ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]])) : item);
  const props = line => line.properties || {};
  const signature = line => stable([id(line), props(line), line.selling_plan_allocation?.selling_plan?.id || null, line.parent_relationship || null, line.has_components || false, (line.item_components || []).map(child => [signature(child), child.quantity])]);
  const quantities = cart => {
    const result = {};
    cart.items.forEach(line => { const key = signature(line); result[key] = (result[key] || 0) + line.quantity; });
    return result;
  };
  const context = cart => stable([cart.currency, cart.note || '', cart.attributes || {}]);
  const same = (cart, expected, baseline) => stable(quantities(cart)) === stable(expected) && context(cart) === context(baseline);
  const replacement = line => ({variant_id: Number(NEW), properties: props(line), quantity: line.quantity});
  const classify = cart => {
    if (!cart.items.some(line => PFX.has(id(line)))) return {candidates: [], blocked: false};
    const candidates = [];
    let blocked = false;
    const inspect = (line, parent) => {
      if (parent && id(parent) === XR_BUNDLE) return;
      if (LEGACY.has(id(line)) || id(line) === OLD) {
        const relation = line.parent_relationship;
        const related = relation && cart.items.find(item => item.key === relation.parent_key || item.key === relation.parent?.key);
        if (related && id(related) === XR_BUNDLE) return;
        const grouped = parent || relation || line.has_components || Object.keys(props(line)).some(key => /bundle|parent/i.test(key));
        if (LEGACY.has(id(line)) || grouped || line.selling_plan_allocation || !Number.isSafeInteger(line.quantity) || line.quantity < 1) blocked = true;
        else candidates.push(line);
      }
      (line.item_components || []).forEach(child => inspect(child, line));
    };
    cart.items.forEach(line => inspect(line, null));
    return {candidates, blocked};
  };
  const expectedStates = (cart, candidates) => {
    const added = quantities(cart);
    const final = quantities(cart);
    candidates.forEach(line => {
      const before = signature(line);
      const after = signature(replacement(line));
      added[after] = (added[after] || 0) + line.quantity;
      final[after] = (final[after] || 0) + line.quantity;
      final[before] -= line.quantity;
      if (!final[before]) delete final[before];
    });
    return {added, final};
  };
  const correct = async (baseline, targetPrice, api) => {
    const {candidates, blocked} = classify(baseline);
    if (blocked || candidates.some(line => line.original_price !== targetPrice)) throw new Error('review');
    if (!candidates.length) return {cart: baseline, changed: false};
    const states = expectedStates(baseline, candidates);
    const sourceKeys = new Set(candidates.map(signature));
    const fresh = await api.get();
    if (!same(fresh, quantities(baseline), baseline) || fresh.total_price !== baseline.total_price) throw new Error('review');
    try {
      await api.add(candidates.map(line => ({id: Number(NEW), quantity: line.quantity, properties: props(line)})));
      const added = await api.get();
      if (!same(added, states.added, baseline)) throw new Error('review');
      const updates = {};
      added.items.forEach(line => { if (sourceKeys.has(signature(line))) updates[line.key] = 0; });
      await api.update(updates);
      const final = await api.get();
      if (!same(final, states.final, baseline) || final.total_price !== baseline.total_price || final.original_total_price !== baseline.original_total_price) throw new Error('review');
      return {cart: final, changed: true};
    } catch (error) {
      try {
        let current = await api.get();
        if (same(current, states.final, baseline)) {
          await api.add(candidates.map(line => ({id: Number(OLD), quantity: line.quantity, properties: props(line)})));
          current = await api.get();
        }
        if (same(current, states.added, baseline)) {
          const remaining = quantities(baseline);
          const targetKeys = new Set(candidates.map(line => signature(replacement(line))));
          const updates = {};
          current.items.forEach(line => {
            const key = signature(line);
            if (!targetKeys.has(key)) return;
            const keep = Math.min(line.quantity, remaining[key] || 0);
            remaining[key] = (remaining[key] || 0) - keep;
            updates[line.key] = keep;
          });
          await api.update(updates);
          current = await api.get();
        }
        error.restored = same(current, quantities(baseline), baseline) && current.total_price === baseline.total_price;
      } catch (_) {}
      throw error;
    }
  };
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {classify, correct, quantities, expectedStates, signature, same};
    return;
  }
  const script = document.currentScript;
  const root = window.Shopify?.routes?.root || '/';
  const cartURL = root + 'cart';
  const helpURL = root + 'products/arcade2tv-pinball-fx-edition?setup=pedestal&variant=53219249291546';
  const messages = script.dataset;
  const accelerated = new Map();
  let conflict = false;
  let running = null;
  let checkoutPending = false;
  let debounce;
  let failed = false;
  let uncertain = false;
  let replaying = false;
  try { uncertain = sessionStorage.getItem('pfx-pedestal-review') === '1'; } catch (_) {}
  const json = async (url, body) => {
    const response = await fetch(url, body ? {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)} : {cache: 'no-store'});
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error('review');
    return data;
  };
  const api = {get: () => json(root + 'cart.js'), add: items => json(root + 'cart/add.js', {items}), update: updates => json(root + 'cart/update.js', {updates})};
  const hideAccelerated = value => {
    conflict = value;
    if (value) {
      document.querySelectorAll('.additional-checkout-buttons, shopify-accelerated-checkout-cart').forEach(element => {
        if (!accelerated.has(element)) accelerated.set(element, {hidden: element.hidden, inert: element.inert, display: element.style.getPropertyValue('display'), priority: element.style.getPropertyPriority('display')});
        element.hidden = true;
        element.inert = true;
        element.style.setProperty('display', 'none', 'important');
      });
    } else {
      accelerated.forEach((saved, element) => {
        element.hidden = saved.hidden;
        element.inert = saved.inert;
        if (saved.display) element.style.setProperty('display', saved.display, saved.priority);
        else element.style.removeProperty('display');
      });
      accelerated.clear();
    }
  };
  const show = (message, error) => {
    document.querySelectorAll('.sf-cart__buttons-container, .scd__footer').forEach(host => {
      let notice = host.querySelector('[data-pfx-pedestal-notice]');
      if (!notice) {
        notice = document.createElement('p');
        notice.dataset.pfxPedestalNotice = '';
        notice.style.cssText = 'font-size:14px;line-height:1.5;margin:12px 0;';
        host.prepend(notice);
      }
      notice.setAttribute('role', error ? 'alert' : 'status');
      notice.textContent = message;
      if (error) {
        const link = document.createElement('a');
        link.href = helpURL;
        link.textContent = messages.help;
        link.style.textDecoration = 'underline';
        notice.append(' ', link);
      }
    });
  };
  const busy = () => !!document.querySelector('.sf-cart[aria-busy="true"], .scd__wrapper[aria-busy="true"]');
  const settle = async () => {
    for (let attempt = 0; attempt < 80; attempt++) {
      if (!busy()) { await new Promise(resolve => setTimeout(resolve, 150)); if (!busy()) return; }
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    throw new Error('review');
  };
  const pendingQuantities = cart => [...document.querySelectorAll('input[name="updates[]"][data-id]')].some(input => {
    if (!input.getClientRects().length) return false;
    const line = cart.items.find(item => item.key === input.dataset.id);
    return !line || Number(input.value) !== line.quantity;
  });
  const check = () => {
    if (running) return running;
    running = (async () => {
      await settle();
      const cart = await api.get();
      const result = classify(cart);
      if (uncertain && cart.items.some(line => PFX.has(id(line))) && cart.items.some(line => id(line) === OLD || id(line) === NEW)) throw new Error('review');
      hideAccelerated(result.blocked || result.candidates.length > 0);
      if (!conflict) {
        failed = false;
        uncertain = false;
        try { sessionStorage.removeItem('pfx-pedestal-review'); } catch (_) {}
        return {changed: false};
      }
      show(messages.checking, false);
      if (uncertain || result.blocked || pendingQuantities(cart)) throw new Error('review');
      const product = await json(root + 'products/arcade2tv-pinball-fx-edition-pedestal.js');
      const target = product.variants.find(variant => String(variant.id) === NEW);
      if (!target || !target.available) throw new Error('review');
      await settle();
      const controls = [...document.querySelectorAll('.sf-cart button:not([disabled]), .sf-cart input:not([disabled]), #cart-drawer-form button:not([disabled]), #cart-drawer-form input:not([disabled])')];
      controls.forEach(control => { control.disabled = true; });
      try {
        uncertain = true;
        try { sessionStorage.setItem('pfx-pedestal-review', '1'); } catch (_) {}
        const corrected = await correct(cart, target.price, api);
        uncertain = false;
        try { sessionStorage.removeItem('pfx-pedestal-review'); } catch (_) {}
        failed = false;
        hideAccelerated(false);
        show(messages.success, false);
        if (corrected.changed) {
          try { sessionStorage.setItem('pfx-pedestal-corrected', '1'); } catch (_) {}
        }
        return corrected;
      } catch (error) {
        if (error.restored) {
          uncertain = false;
          try { sessionStorage.removeItem('pfx-pedestal-review'); } catch (_) {}
        }
        throw error;
      } finally {
        controls.forEach(control => { control.disabled = false; });
      }
    })().catch(error => { failed = true; hideAccelerated(true); show(messages.review, true); throw error; }).finally(() => { running = null; });
    return running;
  };
  const schedule = () => {
    clearTimeout(debounce);
    debounce = setTimeout(() => {
      if (failed || checkoutPending) return;
      check().then(result => { if (result.changed && !checkoutPending) window.location.assign(cartURL); }).catch(() => {});
    }, 250);
  };
  const checkout = async (event, form, button) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutPending) return;
    if (form && !form.checkValidity()) { form.reportValidity(); return; }
    checkoutPending = true;
    try {
      const result = await check();
      if (result.changed) window.location.assign(cartURL);
      else {
        replaying = true;
        if (event.type === 'click') button.click();
        else form.requestSubmit(button && button.form === form ? button : undefined);
        replaying = false;
        checkoutPending = false;
      }
    } catch (_) { checkoutPending = false; }
  };
  document.addEventListener('click', event => {
    if (replaying) return;
    const button = event.target.closest('button[name="checkout"], input[name="checkout"], a[href]');
    if (!button) return;
    if (button.matches('a')) {
      const url = new URL(button.href, window.location.href);
      if (url.origin !== window.location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
    }
    checkout(event, button.form, button);
  }, true);
  document.addEventListener('submit', event => {
    if (!replaying && event.submitter?.name === 'checkout') checkout(event, event.target, event.submitter);
  }, true);
  document.addEventListener('change', event => {
    if (event.target.closest('.sf-cart, #cart-drawer-form')) { failed = false; schedule(); }
  }, true);
  const start = () => {
    new MutationObserver(() => { if (conflict) hideAccelerated(true); }).observe(document.body, {childList: true, subtree: true});
    if (window.MinimogEvents?.subscribe) {
      window.MinimogEvents.subscribe('ON_CART_UPDATE', () => { failed = false; schedule(); });
      window.MinimogEvents.subscribe('ON_ITEM_ADDED', () => { failed = false; schedule(); });
    }
    try { if (sessionStorage.getItem('pfx-pedestal-corrected')) { sessionStorage.removeItem('pfx-pedestal-corrected'); show(messages.success, false); } } catch (_) {}
    schedule();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
